import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Construction, Sparkles, Calendar, Mail, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/launch`;

/**
 * Reusable, polished "coming soon / in development" placeholder with a built-in
 * interest-capture form (writes to placeholder_interest via /api/launch/interest).
 */
export default function PlaceholderPage({
  title,
  doorLabel,
  status = "In Development",
  purpose = "",
  expectedFunction = [],
  back = "/",
  contactEmail = "",
  testId,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const notify = async () => {
    if (!email.trim()) return toast.error("Please enter your email.");
    setBusy(true);
    try {
      await axios.post(`${API}/interest`, {
        email: email.trim().toLowerCase(), name: name.trim(),
        route: location.pathname, door: doorLabel || "", feature: title,
      });
      setSent(true);
      toast.success("We'll notify you when this is ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not capture interest");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF8F2]" data-testid="placeholder-page" data-variant={testId || "default"}>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate(back)} className="text-sm text-slate-600 hover:text-purple-900 inline-flex items-center gap-1.5" data-testid="placeholder-back">
            <ArrowLeft className="w-3.5 h-3.5" />Back
          </button>
          <div className="text-[11px] uppercase tracking-wide text-slate-500">{doorLabel || "KCIS"}</div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-16 text-center">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-purple-900 bg-purple-50 border border-purple-200 px-3 py-1 rounded-full mb-4">
          <Construction className="w-3.5 h-3.5" />{status}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-slate-900 leading-tight">{title}</h1>
        {purpose && <p className="mt-4 text-base text-slate-600 max-w-2xl mx-auto">{purpose}</p>}
        {expectedFunction.length > 0 && (
          <div className="mt-8 max-w-xl mx-auto text-left rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-3 inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-purple-900" />Expected function
            </div>
            <ul className="space-y-2 text-sm text-slate-700">
              {expectedFunction.map((line, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-purple-900 mt-0.5">›</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-10 max-w-xl mx-auto rounded-2xl border border-purple-200 bg-purple-50/40 p-6" data-testid="placeholder-interest">
          {sent ? (
            <div className="text-sm text-emerald-700 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />Thanks — we'll let you know.</div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-wide text-purple-900 mb-2">Be the first to know</div>
              <div className="text-sm text-slate-700 mb-4">Drop your details and we'll email you the moment this is live.</div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className="md:col-span-4 rounded-xl border border-slate-200 h-10 px-3 text-sm" data-testid="placeholder-name" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="md:col-span-6 rounded-xl border border-slate-200 h-10 px-3 text-sm" data-testid="placeholder-email" />
                <button onClick={notify} disabled={busy} className="md:col-span-2 rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-10 text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60" data-testid="placeholder-submit">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}Notify
                </button>
              </div>
            </>
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to={back} className="rounded-xl border border-slate-200 bg-white px-4 h-10 inline-flex items-center text-sm hover:border-purple-300" data-testid="placeholder-back-link">
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />Return
          </Link>
          {contactEmail && (
            <a href={`mailto:${contactEmail}?subject=Interest in ${encodeURIComponent(title)}`} className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white px-4 h-10 inline-flex items-center text-sm" data-testid="placeholder-contact">
              <Mail className="w-3.5 h-3.5 mr-1.5" />Email us
            </a>
          )}
        </div>
        <div className="mt-12 text-[11px] text-slate-400 inline-flex items-center gap-1.5">
          <Calendar className="w-3 h-3" />Target: post-launch (see KCIS roadmap)
        </div>
      </main>
    </div>
  );
}
